import {
	computed,
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	HostListener,
	Input,
	OnDestroy,
	Output,
	Renderer2,
	signal
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';
import {
	REORDER_FOCUS_SETTLE_DELAY_MS,
	REORDER_LIVE_REGION_DELAY_MS
} from '@go-modules/directives/accessible-drop-list/accessible-drop-list.constants';

export type AccessibleGridReorderItemId = number | string;
export type AccessibleGridReorderFocusTarget = 'row' | string;

export interface AccessibleGridReorderMoveEvent {
	itemId: AccessibleGridReorderItemId;
	currentIndex: number;
	newIndex: number;
}

export interface AccessibleGridReorderFinalizeEvent {
	itemId: AccessibleGridReorderItemId;
	currentIndex: number;
	originalIndex: number;
	moved: boolean;
}

export interface AccessibleGridReorderCancelEvent {
	itemId: AccessibleGridReorderItemId;
	currentIndex: number;
	originalIndex: number;
}

interface AccessibleGridReorderFocusState {
	itemId: AccessibleGridReorderItemId;
	target: AccessibleGridReorderFocusTarget;
}

/**
 * Reorderable composite tables/grids need row-level navigation before cell
 * navigation. This directive owns roving focus, grab/move/drop announcements,
 * and NVDA-safe focus restoration for grids with multiple interactive cells
 * per row.
 */
@Directive({
	selector: '[accessibleGridReorder]',
	exportAs: 'accessibleGridReorder',
	standalone: true
})
export class AccessibleGridReorderDirective implements OnDestroy {
	private readonly itemIds = signal<AccessibleGridReorderItemId[]>([]);
	private readonly columnKeys = signal<string[]>([]);
	private readonly primaryTarget = signal('handle');
	private readonly initialTarget = signal<AccessibleGridReorderFocusTarget>('row');
	private hasExplicitFocusTarget = false;

	public readonly focusedItemId = signal<AccessibleGridReorderItemId | null>(null);
	public readonly focusedTarget = signal<AccessibleGridReorderFocusTarget>(
		this.initialTarget()
	);
	public readonly grabbedItemId = signal<AccessibleGridReorderItemId | null>(null);
	public readonly columnCount = computed(() => this.columnKeys().length);
	private readonly interactionState = signal<AccessibleGridReorderFocusState | null>(null);

	private originalIndex: Record<string, number> = {};
	private focusTimeout: ReturnType<typeof setTimeout> | null = null;
	private focusOutTimeout: ReturnType<typeof setTimeout> | null = null;
	private liveRegionTimeout: ReturnType<typeof setTimeout> | null = null;
	private liveRegionElement: HTMLElement | null = null;
	private liveRegionHost: HTMLElement | null = null;

	@Output()
	public readonly accessibleGridReorderMoveRequested =
		new EventEmitter<AccessibleGridReorderMoveEvent>();
	@Output()
	public readonly accessibleGridReorderFinalize =
		new EventEmitter<AccessibleGridReorderFinalizeEvent>();
	@Output()
	public readonly accessibleGridReorderCancel =
		new EventEmitter<AccessibleGridReorderCancelEvent>();

	@HostBinding('attr.role') public readonly role = 'grid';

	@Input()
	public set accessibleGridReorderItems (itemIds: AccessibleGridReorderItemId[] | null) {
		this.itemIds.set(itemIds ?? []);
		this.syncStateFromItems();
	}

	@Input()
	public set accessibleGridReorderColumns (columnKeys: readonly string[] | null) {
		this.columnKeys.set(columnKeys == null ? [] : [...columnKeys]);
		this.ensureFocusedTargetIsReachable();
	}

	@Input()
	public set accessibleGridReorderPrimaryTarget (target: string) {
		const nextTarget = target || 'handle';
		this.primaryTarget.set(nextTarget);
		this.ensureFocusedTargetIsReachable();
	}

	public get accessibleGridReorderPrimaryTarget (): string {
		return this.primaryTarget();
	}

	@Input()
	public set accessibleGridReorderInitialTarget (
		target: AccessibleGridReorderFocusTarget | null
	) {
		const nextTarget = target == null ? 'row' : target;
		this.initialTarget.set(nextTarget);
		this.ensureFocusedTargetIsReachable();
		if (!this.hasExplicitFocusTarget && this.grabbedItemId() == null) {
			this.focusedTarget.set(this.getDefaultFocusTarget());
		}
	}

	public get accessibleGridReorderInitialTarget (): AccessibleGridReorderFocusTarget {
		return this.initialTarget();
	}

	@Input() public accessibleGridReorderLabelledby: string | null = null;
	@Input() public accessibleGridReorderDescribedby: string | null = null;
	@Input() public accessibleGridReorderUseLiveRegionFallback = false;
	@Input()
	public accessibleGridReorderGetItemLabel: (itemId: AccessibleGridReorderItemId) => string = () => '';

	constructor (
		private readonly elementRef: ElementRef<HTMLElement>,
		private readonly liveAnnouncer: LiveAnnouncer,
		private readonly renderer: Renderer2,
		private readonly translate: TranslateService
	) {}

	@HostBinding('attr.aria-labelledby')
	public get ariaLabelledby (): string | null {
		return this.accessibleGridReorderLabelledby;
	}

	@HostBinding('attr.aria-describedby')
	public get ariaDescribedby (): string | null {
		return this.accessibleGridReorderDescribedby;
	}

	@HostBinding('attr.aria-colcount')
	public get ariaColcount (): string | null {
		const columnCount = this.columnCount();
		return columnCount > 0 ? columnCount.toString() : null;
	}

	public ngOnDestroy (): void {
		this.clearFocusTimeout();
		this.clearFocusOutTimeout();
		this.clearLiveRegionTimeout();

		if (this.liveRegionElement != null && this.liveRegionHost != null) {
			this.renderer.removeChild(this.liveRegionHost, this.liveRegionElement);
			this.liveRegionElement = null;
			this.liveRegionHost = null;
		}
	}

	public getRowTabindex (itemId: AccessibleGridReorderItemId): number {
		const activeState = this.getActiveFocusState();
		if (activeState == null) {
			return -1;
		}

		return this.areSameItemId(activeState.itemId, itemId) &&
			activeState.target === 'row' ? 0 : -1;
	}

	public getCellTabindex (
		itemId: AccessibleGridReorderItemId,
		target: string
	): number {
		const activeState = this.getActiveFocusState();
		if (activeState == null) {
			return -1;
		}

		return this.areSameItemId(activeState.itemId, itemId) &&
			activeState.target === target ? 0 : -1;
	}

	public isGrabbed (itemId: AccessibleGridReorderItemId): boolean {
		return this.areSameItemId(this.grabbedItemId(), itemId);
	}

	public handleRowFocus (itemId: AccessibleGridReorderItemId): void {
		this.hasExplicitFocusTarget = true;
		this.clearInteractionState();
		const grabbedItemId = this.grabbedItemId();
		if (grabbedItemId != null && !this.areSameItemId(grabbedItemId, itemId)) {
			this.cancelGrabbedReorder(false, false);
		}

		this.focusedItemId.set(itemId);
		this.focusedTarget.set('row');
	}

	public handleCellFocus (
		itemId: AccessibleGridReorderItemId,
		target: string,
		event?: FocusEvent
	): void {
		this.hasExplicitFocusTarget = true;
		const grabbedItemId = this.grabbedItemId();
		const leftGrabbedReorderTarget = grabbedItemId != null &&
			(
				!this.areSameItemId(grabbedItemId, itemId) ||
				!this.isReorderFocusTarget(target)
			);

		if (leftGrabbedReorderTarget) {
			this.cancelGrabbedReorder(false, false);
		}

		this.focusedItemId.set(itemId);
		this.focusedTarget.set(target);
		if (this.isNestedCellInteractionFocusEvent(itemId, target, event)) {
			this.interactionState.set({
				itemId,
				target
			});
			return;
		}

		this.clearInteractionState();
	}

	public handleRowKeyDown (
		event: KeyboardEvent,
		itemId: AccessibleGridReorderItemId
	): void {
		if (event.target !== event.currentTarget) {
			return;
		}

		const itemIndex = this.findItemIndex(itemId);
		if (itemIndex < 0) {
			return;
		}

		if (this.isArrowDownKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.requestMove(itemId, 1);
			} else {
				this.focusRowByIndex(itemIndex + 1);
			}
			return;
		}

		if (this.isArrowUpKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.requestMove(itemId, -1);
			} else {
				this.focusRowByIndex(itemIndex - 1);
			}
			return;
		}

		if (this.isArrowRightKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (!this.isGrabbed(itemId)) {
				this.focusFirstCell(itemId);
			}
			return;
		}

		if (this.isArrowLeftKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (this.isHomeKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowByIndex(0);
			return;
		}

		if (this.isEndKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowByIndex(this.itemIds().length - 1);
			return;
		}

		if (this.isSelectKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.finalizeGrabbedReorder();
			} else {
				this.startGrabbedReorder(itemId, 'row');
			}
			return;
		}

		if (this.isEscapeKey(event) && this.isGrabbed(itemId)) {
			event.preventDefault();
			event.stopPropagation();
			this.cancelGrabbedReorder();
		}
	}

	public handleReorderCellKeyDown (
		event: KeyboardEvent,
		itemId: AccessibleGridReorderItemId
	): void {
		if (this.isArrowDownKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.requestMove(itemId, 1);
			} else {
				this.focusRowTargetByIndex(
					this.findItemIndex(itemId) + 1,
					this.accessibleGridReorderPrimaryTarget
				);
			}
			return;
		}

		if (this.isArrowUpKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.requestMove(itemId, -1);
			} else {
				this.focusRowTargetByIndex(
					this.findItemIndex(itemId) - 1,
					this.accessibleGridReorderPrimaryTarget
				);
			}
			return;
		}

		if (this.isArrowRightKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (!this.isGrabbed(itemId)) {
				this.focusNextTarget(itemId, this.accessibleGridReorderPrimaryTarget);
			}
			return;
		}

		if (this.isArrowLeftKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (!this.isGrabbed(itemId)) {
				this.focusRowByItemId(itemId);
			}
			return;
		}

		if (this.isHomeKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(0, this.accessibleGridReorderPrimaryTarget);
			return;
		}

		if (this.isEndKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(
				this.itemIds().length - 1,
				this.accessibleGridReorderPrimaryTarget
			);
			return;
		}

		if (this.isSelectKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			if (this.isGrabbed(itemId)) {
				this.finalizeGrabbedReorder();
			} else {
				this.startGrabbedReorder(
					itemId,
					this.accessibleGridReorderPrimaryTarget
				);
			}
			return;
		}

		if (this.isEscapeKey(event) && this.isGrabbed(itemId)) {
			event.preventDefault();
			event.stopPropagation();
			this.cancelGrabbedReorder();
		}
	}

	public handleCellKeyDown (
		event: KeyboardEvent,
		itemId: AccessibleGridReorderItemId,
		target: string
	): void {
		if (this.isInteractionMode(itemId, target, event)) {
			if (this.isEscapeKey(event) || this.isF2Key(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitCellInteractionMode(itemId, target);
				return;
			}

			if (this.isArrowDownKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusRowTargetByIndex(this.findItemIndex(itemId) + 1, target)
				);
				return;
			}

			if (this.isArrowUpKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusRowTargetByIndex(this.findItemIndex(itemId) - 1, target)
				);
				return;
			}

			if (this.isArrowRightKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusNextTarget(itemId, target)
				);
				return;
			}

			if (this.isArrowLeftKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusPreviousTarget(itemId, target)
				);
				return;
			}

			if (this.isHomeKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusRowTargetByIndex(0, target)
				);
				return;
			}

			if (this.isEndKey(event)) {
				event.preventDefault();
				event.stopPropagation();
				this.exitInteractionModeToTarget(
					itemId,
					target,
					() => this.focusRowTargetByIndex(this.itemIds().length - 1, target)
				);
			}
			return;
		}

		if (this.tryDirectCellActivation(itemId, target, event)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if ((this.isEnterKey(event) || this.isF2Key(event)) &&
			this.enterCellInteractionMode(itemId, target)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (this.isArrowDownKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(this.findItemIndex(itemId) + 1, target);
			return;
		}

		if (this.isArrowUpKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(this.findItemIndex(itemId) - 1, target);
			return;
		}

		if (this.isArrowRightKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusNextTarget(itemId, target);
			return;
		}

		if (this.isArrowLeftKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusPreviousTarget(itemId, target);
			return;
		}

		if (this.isHomeKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(0, target);
			return;
		}

		if (this.isEndKey(event)) {
			event.preventDefault();
			event.stopPropagation();
			this.focusRowTargetByIndex(this.itemIds().length - 1, target);
		}
	}

	public handleExternalDrop (itemId: AccessibleGridReorderItemId): void {
		const currentIndex = this.findItemIndex(itemId);
		if (currentIndex < 0) {
			return;
		}

		const defaultFocusTarget = this.getDefaultFocusTarget();
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(defaultFocusTarget);
		this.commitCurrentOrderAsOriginal();
		this.scheduleFocusTarget(itemId, defaultFocusTarget);
		this.announce('aria-ng-sortable_dropped', {
			name: this.accessibleGridReorderGetItemLabel(itemId),
			position: currentIndex + 1,
			count: this.itemIds().length
		});
	}

	public finalizeActiveReorder (
		announcePosition = true,
		restoreFocus = true
	): void {
		this.finalizeGrabbedReorder(announcePosition, restoreFocus);
	}

	public cancelActiveReorder (
		announcePosition = true,
		restoreFocus = true
	): void {
		this.cancelGrabbedReorder(announcePosition, restoreFocus);
	}

	@HostListener('focusout')
	public handleHostFocusOut (): void {
		const interactionState = this.interactionState();
		if (this.grabbedItemId() == null && interactionState == null) {
			return;
		}

		this.clearFocusOutTimeout();
		/**
		 * Chrome + NVDA can briefly report `body` or the dialog container as the
		 * active element while focus is being restored after a reorder. Waiting
		 * for the shared settle window avoids cancelling a valid in-progress drag.
		 */
		this.focusOutTimeout = setTimeout(() => {
			this.focusOutTimeout = null;
			const activeElement = this.elementRef.nativeElement.ownerDocument
				.activeElement as Element | null;
			if (this.elementRef.nativeElement.contains(activeElement)) {
				return;
			}

			const currentInteractionState = this.interactionState();
			if (currentInteractionState != null &&
				(
					activeElement == null ||
					activeElement === this.elementRef.nativeElement.ownerDocument.body
				)
			) {
				this.exitCellInteractionMode(
					currentInteractionState.itemId,
					currentInteractionState.target as string
				);
				return;
			}

			this.cancelGrabbedReorder(true, false);
		}, REORDER_FOCUS_SETTLE_DELAY_MS);
	}

	private syncStateFromItems (): void {
		const itemIds = this.itemIds();
		if (itemIds.length === 0) {
			this.hasExplicitFocusTarget = false;
			this.focusedItemId.set(null);
			this.grabbedItemId.set(null);
			this.clearInteractionState();
			this.originalIndex = {};
			return;
		}

		const focusedItemId = this.focusedItemId();
		const nextFocusedItemId = focusedItemId != null &&
			itemIds.some((currentItemId) => this.areSameItemId(currentItemId, focusedItemId))
			? focusedItemId
			: itemIds[0];

		if (!this.areSameItemId(nextFocusedItemId, focusedItemId)) {
			this.focusedItemId.set(nextFocusedItemId);
		}

		if (!this.hasExplicitFocusTarget) {
			this.focusedTarget.set(this.getDefaultFocusTarget());
		}

		this.ensureFocusedTargetIsReachable();
		this.syncInteractionStateFromItems();

		if (this.grabbedItemId() == null) {
			this.commitCurrentOrderAsOriginal();
		}
	}

	private ensureFocusedTargetIsReachable (): void {
		const focusedTarget = this.focusedTarget();
		if (focusedTarget === 'row' || this.columnKeys().includes(focusedTarget)) {
			return;
		}

		this.focusedTarget.set(this.getDefaultFocusTarget());
	}

	private syncInteractionStateFromItems (): void {
		const interactionState = this.interactionState();
		if (interactionState == null) {
			return;
		}

		const interactionItemExists = this.itemIds().some((itemId) =>
			this.areSameItemId(itemId, interactionState.itemId)
		);
		const interactionTargetExists = interactionState.target === 'row' ||
			this.columnKeys().includes(interactionState.target);
		if (interactionItemExists && interactionTargetExists) {
			return;
		}

		this.clearInteractionState();
	}

	private getDefaultFocusTarget (): AccessibleGridReorderFocusTarget {
		const initialTarget = this.initialTarget();
		if (initialTarget === 'row') {
			return 'row';
		}

		return this.columnKeys().includes(initialTarget)
			? initialTarget
			: this.accessibleGridReorderPrimaryTarget;
	}

	private getCurrentReorderTarget (
		target = this.focusedTarget()
	): AccessibleGridReorderFocusTarget {
		return this.isReorderFocusTarget(target)
			? target
			: this.getDefaultFocusTarget();
	}

	private isReorderFocusTarget (
		target: AccessibleGridReorderFocusTarget
	): boolean {
		return target === 'row' || target === this.accessibleGridReorderPrimaryTarget;
	}

	private startGrabbedReorder (
		itemId: AccessibleGridReorderItemId,
		target: AccessibleGridReorderFocusTarget
	): void {
		const currentIndex = this.findItemIndex(itemId);
		if (currentIndex < 0) {
			return;
		}

		const reorderTarget = this.getCurrentReorderTarget(target);
		this.grabbedItemId.set(itemId);
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(reorderTarget);
		this.announce('aria-ng-sortable_grabbed', {
			name: this.accessibleGridReorderGetItemLabel(itemId),
			position: currentIndex + 1,
			count: this.itemIds().length
		});
	}

	private requestMove (
		itemId: AccessibleGridReorderItemId,
		offset: -1 | 1
	): void {
		if (!this.isGrabbed(itemId)) {
			return;
		}

		const currentIndex = this.findItemIndex(itemId);
		const newIndex = currentIndex + offset;
		if (newIndex < 0 || newIndex >= this.itemIds().length) {
			return;
		}

		const reorderTarget = this.getCurrentReorderTarget();
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(reorderTarget);
		this.scheduleFocusTarget(itemId, reorderTarget);
		this.announce('aria-ng-sortable_moved', {
			name: this.accessibleGridReorderGetItemLabel(itemId),
			position: newIndex + 1,
			count: this.itemIds().length
		});
		this.accessibleGridReorderMoveRequested.emit({
			itemId,
			currentIndex,
			newIndex
		});
	}

	private finalizeGrabbedReorder (
		announcePosition = true,
		restoreFocus = true
	): void {
		const itemId = this.grabbedItemId();
		if (itemId == null) {
			return;
		}

		const currentIndex = this.findItemIndex(itemId);
		const originalIndex = this.originalIndex[this.itemIdKey(itemId)];
		const moved = currentIndex !== originalIndex;
		const reorderTarget = this.getCurrentReorderTarget();

		this.grabbedItemId.set(null);
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(reorderTarget);

		if (announcePosition) {
			this.announce('aria-ng-sortable_dropped', {
				name: this.accessibleGridReorderGetItemLabel(itemId),
				position: currentIndex + 1,
				count: this.itemIds().length
			});
		}

		this.commitCurrentOrderAsOriginal();
		this.accessibleGridReorderFinalize.emit({
			itemId,
			currentIndex,
			originalIndex,
			moved
		});

		if (restoreFocus) {
			this.scheduleFocusTarget(itemId, reorderTarget);
		}
	}

	private cancelGrabbedReorder (
		announcePosition = true,
		restoreFocus = true
	): void {
		const itemId = this.grabbedItemId();
		if (itemId == null) {
			return;
		}

		const currentIndex = this.findItemIndex(itemId);
		const originalIndex = this.originalIndex[this.itemIdKey(itemId)];
		const reorderTarget = this.getCurrentReorderTarget();

		this.grabbedItemId.set(null);
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(reorderTarget);

		if (announcePosition) {
			this.announce('aria-ng-sortable_cancelled', {
				name: this.accessibleGridReorderGetItemLabel(itemId)
			});
		}

		this.accessibleGridReorderCancel.emit({
			itemId,
			currentIndex,
			originalIndex
		});

		if (restoreFocus) {
			this.scheduleFocusTarget(itemId, reorderTarget);
		}
	}

	private focusRowByIndex (itemIndex: number): void {
		const itemIds = this.itemIds();
		if (itemIndex < 0 || itemIndex >= itemIds.length) {
			return;
		}

		this.focusRowByItemId(itemIds[itemIndex]);
	}

	private focusRowByItemId (itemId: AccessibleGridReorderItemId): void {
		this.focusTarget(itemId, 'row');
	}

	private focusRowTargetByIndex (
		itemIndex: number,
		target: AccessibleGridReorderFocusTarget
	): void {
		const itemIds = this.itemIds();
		if (itemIndex < 0 || itemIndex >= itemIds.length) {
			return;
		}

		const itemId = itemIds[itemIndex];
		if (!this.focusTarget(itemId, target)) {
			this.focusRowByItemId(itemId);
		}
	}

	private focusFirstCell (itemId: AccessibleGridReorderItemId): void {
		this.focusPreferredTarget(itemId, [
			this.accessibleGridReorderPrimaryTarget,
			...this.getNextTargets(this.accessibleGridReorderPrimaryTarget)
		]);
	}

	private focusNextTarget (
		itemId: AccessibleGridReorderItemId,
		target: string
	): void {
		this.focusPreferredTarget(itemId, this.getNextTargets(target));
	}

	private focusPreviousTarget (
		itemId: AccessibleGridReorderItemId,
		target: string
	): void {
		this.focusPreferredTarget(itemId, [
			...this.getPreviousTargets(target),
			'row'
		]);
	}

	private focusPreferredTarget (
		itemId: AccessibleGridReorderItemId,
		targets: AccessibleGridReorderFocusTarget[]
	): void {
		targets.some((target) => this.focusTarget(itemId, target));
	}

	private focusTarget (
		itemId: AccessibleGridReorderItemId,
		target: AccessibleGridReorderFocusTarget
	): boolean {
		const focusableElement = this.getFocusableElement(itemId, target);
		if (focusableElement == null) {
			return false;
		}

		this.clearInteractionState();
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(target);
		focusableElement.focus();
		return true;
	}

	private scheduleFocusTarget (
		itemId: AccessibleGridReorderItemId,
		target: AccessibleGridReorderFocusTarget
	): void {
		this.clearFocusTimeout();
		this.focusTimeout = setTimeout(() => {
			this.focusTarget(itemId, target);
			this.focusTimeout = null;
		}, 0);
	}

	private getFocusableElement (
		itemId: AccessibleGridReorderItemId,
		target: AccessibleGridReorderFocusTarget
	): HTMLElement | null {
		const row = this.getRowElement(itemId);
		if (row == null) {
			return null;
		}

		if (target === 'row') {
			return this.isVisibleFocusableElement(row) ? row : null;
		}

		const cell = this.getCellElement(itemId, target);
		if (cell == null) {
			return null;
		}

		if (cell.hasAttribute('tabindex') &&
			this.isVisibleFocusableElement(cell)) {
			return cell;
		}

		const nestedFocusableElement = this.getNestedFocusableElement(itemId, target);
		return this.isVisibleFocusableElement(nestedFocusableElement)
			? nestedFocusableElement
			: null;
	}

	private enterCellInteractionMode (
		itemId: AccessibleGridReorderItemId,
		target: string
	): boolean {
		const nestedFocusableElement = this.getNestedFocusableElement(itemId, target);
		if (nestedFocusableElement == null) {
			return false;
		}

		if (!this.isCheckboxControl(nestedFocusableElement)) {
			return false;
		}

		/**
		 * NVDA on Windows can consume arrow keys once focus is inside native
		 * buttons or checkboxes. Keep roving focus on the grid cell by default,
		 * and only move DOM focus into the nested control when the user
		 * explicitly enters interaction mode with Enter/F2. Escape/F2 restores
		 * grid navigation on the cell wrapper.
		 */
		this.interactionState.set({
			itemId,
			target
		});
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(target);
		nestedFocusableElement.focus();
		return true;
	}

	private tryDirectCellActivation (
		itemId: AccessibleGridReorderItemId,
		target: string,
		event: KeyboardEvent
	): boolean {
		const nestedFocusableElement = this.getNestedFocusableElement(itemId, target);
		if (nestedFocusableElement == null) {
			return false;
		}

		if (this.isCheckboxControl(nestedFocusableElement)) {
			if (!this.isSelectKey(event)) {
				return false;
			}

			nestedFocusableElement.click();
			this.scheduleFocusTarget(itemId, target);
			return true;
		}

		if (!this.isButtonLikeControl(nestedFocusableElement) || !this.isSelectKey(event)) {
			return false;
		}

		nestedFocusableElement.click();
		return true;
	}

	private exitCellInteractionMode (
		itemId: AccessibleGridReorderItemId,
		target: string
	): void {
		this.clearInteractionState();
		this.focusTarget(itemId, target);
	}

	private exitInteractionModeToTarget (
		itemId: AccessibleGridReorderItemId,
		target: string,
		navigate: () => void
	): void {
		this.clearInteractionState();
		this.focusedItemId.set(itemId);
		this.focusedTarget.set(target);
		navigate();
	}

	private isInteractionMode (
		itemId: AccessibleGridReorderItemId,
		target: string,
		event?: KeyboardEvent
	): boolean {
		const interactionState = this.interactionState();
		const isExplicitInteractionState = interactionState != null &&
			this.areSameItemId(interactionState.itemId, itemId) &&
			interactionState.target === target;
		const targetElementIsNested = event?.target instanceof Element &&
			event?.currentTarget instanceof Element &&
			event.target !== event.currentTarget &&
			event.currentTarget.contains(event.target);

		return isExplicitInteractionState || targetElementIsNested;
	}

	private isNestedCellInteractionFocusEvent (
		itemId: AccessibleGridReorderItemId,
		target: string,
		event?: FocusEvent
	): boolean {
		if (!(event?.target instanceof Element)) {
			return false;
		}

		const cell = this.getCellElement(itemId, target);
		return cell != null &&
			event.target !== cell &&
			cell.contains(event.target);
	}

	private getRowElement (itemId: AccessibleGridReorderItemId): HTMLElement | null {
		return this.elementRef.nativeElement.querySelector(
			`[data-accessible-grid-row-id="${CSS.escape(this.itemIdKey(itemId))}"]`
		) as HTMLElement | null;
	}

	private getCellElement (
		itemId: AccessibleGridReorderItemId,
		target: string
	): HTMLElement | null {
		const row = this.getRowElement(itemId);
		if (row == null) {
			return null;
		}

		return row.querySelector(
			`[data-accessible-grid-cell="${target}"]`
		) as HTMLElement | null;
	}

	private getNestedFocusableElement (
		itemId: AccessibleGridReorderItemId,
		target: string
	): HTMLElement | null {
		const cell = this.getCellElement(itemId, target);
		if (cell == null) {
			return null;
		}

		const candidates = Array.from(
			cell.querySelectorAll(
				[
					'button:not([disabled])',
					'[href]',
					'input:not([type="hidden"])',
					'select',
					'textarea',
					'[tabindex]'
				].join(', ')
			)
		) as HTMLElement[];

		return candidates.find((candidate) => this.isVisibleFocusableElement(candidate)) ?? null;
	}

	private isVisibleFocusableElement (
		element: HTMLElement | null
	): element is HTMLElement {
		if (element == null) {
			return false;
		}

		if ('disabled' in element &&
			(element as HTMLButtonElement | HTMLInputElement).disabled) {
			return false;
		}

		const styles = window.getComputedStyle(element);
		return styles.display !== 'none' && styles.visibility !== 'hidden';
	}

	private isButtonLikeControl (element: HTMLElement): boolean {
		return element instanceof HTMLButtonElement ||
			element instanceof HTMLAnchorElement ||
			element.getAttribute('role') === 'button';
	}

	private isCheckboxControl (element: HTMLElement): element is HTMLInputElement {
		return element instanceof HTMLInputElement && element.type === 'checkbox';
	}

	private getActiveFocusState (): AccessibleGridReorderFocusState | null {
		const itemIds = this.itemIds();
		if (itemIds.length === 0) {
			return null;
		}

		const focusedItemId = this.focusedItemId();
		const activeItemId = focusedItemId != null &&
			itemIds.some((currentItemId) => this.areSameItemId(currentItemId, focusedItemId))
			? focusedItemId
			: itemIds[0];
		const focusedTarget = this.focusedTarget();
		const defaultTarget = this.getDefaultFocusTarget();
		const target = this.getFocusableElement(activeItemId, focusedTarget) == null
			? defaultTarget
			: focusedTarget;

		return {
			itemId: activeItemId,
			target
		};
	}

	private getNextTargets (target: string): string[] {
		const columnKeys = this.columnKeys();
		const targetIndex = columnKeys.indexOf(target);
		return targetIndex < 0 ? [] : columnKeys.slice(targetIndex + 1);
	}

	private getPreviousTargets (target: string): string[] {
		const columnKeys = this.columnKeys();
		const targetIndex = columnKeys.indexOf(target);
		return targetIndex < 0 ? [] : [...columnKeys.slice(0, targetIndex)].reverse();
	}

	private findItemIndex (itemId: AccessibleGridReorderItemId): number {
		return this.itemIds().findIndex(
			(currentItemId) => this.areSameItemId(currentItemId, itemId)
		);
	}

	private commitCurrentOrderAsOriginal (): void {
		this.originalIndex = this.itemIds().reduce<Record<string, number>>(
			(indexMap, itemId, index) => {
				indexMap[this.itemIdKey(itemId)] = index;
				return indexMap;
			},
			{}
		);
	}

	private announce (
		messageKey: string,
		params: Record<string, unknown>
	): void {
		const message = this.translate.instant(messageKey, params);
		if (!this.accessibleGridReorderUseLiveRegionFallback) {
			this.liveAnnouncer.announce(message, 'polite');
			return;
		}

		const liveRegion = this.getOrCreateLiveRegion();
		this.renderer.setAttribute(liveRegion, 'role', 'status');
		this.renderer.setAttribute(liveRegion, 'aria-live', 'polite');
		this.renderer.setProperty(liveRegion, 'textContent', '');
		this.clearLiveRegionTimeout();
		this.liveRegionTimeout = setTimeout(() => {
			this.renderer.setProperty(liveRegion, 'textContent', message);
			this.liveRegionTimeout = null;
		}, REORDER_LIVE_REGION_DELAY_MS);
	}

	private getOrCreateLiveRegion (): HTMLElement {
		if (this.liveRegionElement != null) {
			return this.liveRegionElement;
		}

		const liveRegionHost = this.elementRef.nativeElement.parentElement ??
			this.elementRef.nativeElement;
		const liveRegion = this.renderer.createElement('span') as HTMLElement;
		this.renderer.setAttribute(liveRegion, 'role', 'status');
		this.renderer.setAttribute(liveRegion, 'aria-live', 'polite');
		this.renderer.setAttribute(liveRegion, 'aria-atomic', 'true');
		this.renderer.setStyle(liveRegion, 'position', 'absolute');
		this.renderer.setStyle(liveRegion, 'width', '1px');
		this.renderer.setStyle(liveRegion, 'height', '1px');
		this.renderer.setStyle(liveRegion, 'padding', '0');
		this.renderer.setStyle(liveRegion, 'margin', '-1px');
		this.renderer.setStyle(liveRegion, 'overflow', 'hidden');
		this.renderer.setStyle(liveRegion, 'clip', 'rect(0, 0, 0, 0)');
		this.renderer.setStyle(liveRegion, 'white-space', 'nowrap');
		this.renderer.setStyle(liveRegion, 'border', '0');
		this.renderer.appendChild(liveRegionHost, liveRegion);
		this.liveRegionHost = liveRegionHost;
		this.liveRegionElement = liveRegion;
		return liveRegion;
	}

	private clearFocusTimeout (): void {
		if (this.focusTimeout != null) {
			clearTimeout(this.focusTimeout);
			this.focusTimeout = null;
		}
	}

	private clearFocusOutTimeout (): void {
		if (this.focusOutTimeout != null) {
			clearTimeout(this.focusOutTimeout);
			this.focusOutTimeout = null;
		}
	}

	private clearLiveRegionTimeout (): void {
		if (this.liveRegionTimeout != null) {
			clearTimeout(this.liveRegionTimeout);
			this.liveRegionTimeout = null;
		}
	}

	private itemIdKey (itemId: AccessibleGridReorderItemId): string {
		return String(itemId);
	}

	private areSameItemId (
		left: AccessibleGridReorderItemId | null,
		right: AccessibleGridReorderItemId | null
	): boolean {
		return left != null && right != null && this.itemIdKey(left) === this.itemIdKey(right);
	}

	private isSelectKey (event: KeyboardEvent): boolean {
		return event.key === 'Enter' ||
			event.code === 'Enter' ||
			event.key === ' ' ||
			event.key === 'Spacebar' ||
			event.code === 'Space';
	}

	private isEnterKey (event: KeyboardEvent): boolean {
		return event.key === 'Enter' || event.code === 'Enter';
	}

	private isF2Key (event: KeyboardEvent): boolean {
		return event.key === 'F2' || event.code === 'F2';
	}

	private isArrowDownKey (event: KeyboardEvent): boolean {
		return event.key === 'ArrowDown' || event.code === 'ArrowDown';
	}

	private isArrowUpKey (event: KeyboardEvent): boolean {
		return event.key === 'ArrowUp' || event.code === 'ArrowUp';
	}

	private isArrowRightKey (event: KeyboardEvent): boolean {
		return event.key === 'ArrowRight' || event.code === 'ArrowRight';
	}

	private isArrowLeftKey (event: KeyboardEvent): boolean {
		return event.key === 'ArrowLeft' || event.code === 'ArrowLeft';
	}

	private isHomeKey (event: KeyboardEvent): boolean {
		return event.key === 'Home' || event.code === 'Home';
	}

	private isEndKey (event: KeyboardEvent): boolean {
		return event.key === 'End' || event.code === 'End';
	}

	private isEscapeKey (event: KeyboardEvent): boolean {
		return event.key === 'Escape' || event.key === 'Esc' || event.code === 'Escape';
	}

	private clearInteractionState (): void {
		if (this.interactionState() != null) {
			this.interactionState.set(null);
		}
	}
}
